import { useEffect } from 'react';
import {
  Content,
  ContentHeader,
  SupportButton,
  SubvalueCell,
  Link,
  type TableColumn,
} from '@backstage/core-components';
import {
  EntityListProvider,
  CatalogFilterLayout,
  UserListPicker,
  EntityOwnerPicker,
  EntityTagPicker,
  EntityTypeFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import {
  TechDocsPageWrapper,
  TechDocsPicker,
  EntityListDocsTable,
  type DocsTableRow,
} from '@backstage/plugin-techdocs';

/** Matches the library's own customTitle() in columns.ts (title falls back to name). */
function docTitle(entity: DocsTableRow['entity']): string {
  return entity.metadata.title || entity.metadata.name;
}

/**
 * Replaces EntityListDocsTable's built-in name column so the displayed title
 * is always lowercase, without touching the underlying entity names. Mirrors
 * createNameColumn()'s own sort/render logic exactly, just lowercasing the
 * rendered text.
 */
const lowercaseNameColumn: TableColumn<DocsTableRow> = {
  title: 'Document',
  field: 'entity.metadata.name',
  highlight: true,
  searchable: true,
  defaultSort: 'asc',
  customSort: (row1, row2) =>
    docTitle(row1.entity)
      .toLocaleLowerCase()
      .localeCompare(docTitle(row2.entity).toLocaleLowerCase()),
  render: row => (
    <SubvalueCell
      value={<Link to={row.resolved.docsUrl}>{docTitle(row.entity).toLowerCase()}</Link>}
      subvalue={row.entity.metadata.description}
    />
  ),
};

const docsTableColumns: TableColumn<DocsTableRow>[] = [
  lowercaseNameColumn,
  EntityListDocsTable.columns.createOwnerColumn(),
  EntityListDocsTable.columns.createKindColumn(),
  EntityListDocsTable.columns.createTypeColumn(),
];

/**
 * Permanently applies a `spec.type: documentation` filter via updateFilters,
 * the same mechanism TechDocsPicker itself uses to apply its techdocs-ref
 * filter. Renders nothing -- this only needs to run once, inside the
 * EntityListProvider below, so useEntityList() resolves to the right context.
 */
function DocumentationTypeFilter() {
  const { updateFilters } = useEntityList();
  useEffect(() => {
    updateFilters({ type: new EntityTypeFilter('documentation') });
  }, [updateFilters]);
  return null;
}

/**
 * Mirrors @backstage/plugin-techdocs's DefaultTechDocsHome, but with a
 * permanent documentation-only type filter. DefaultTechDocsHome has no prop
 * for this -- it ignores `children` entirely -- so the only way to inject a
 * permanent filter is to recompose the same building blocks it uses
 * (EntityListProvider, CatalogFilterLayout, TechDocsPicker,
 * EntityListDocsTable, all publicly exported) rather than passing props into
 * TechDocsIndexPage.
 */
export function DocumentationOnlyTechDocsHome() {
  return (
    <TechDocsPageWrapper>
      <Content>
        <ContentHeader title="">
          <SupportButton>Discover documentation in your ecosystem.</SupportButton>
        </ContentHeader>
        <EntityListProvider>
          <DocumentationTypeFilter />
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <TechDocsPicker />
              <UserListPicker initialFilter="owned" />
              <EntityOwnerPicker />
              <EntityTagPicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <EntityListDocsTable columns={docsTableColumns} />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Content>
    </TechDocsPageWrapper>
  );
}
